// src/hooks/useActionFeedback.js
import { useState, useCallback } from 'react';

/**
 * Hook for managing action feedback (success/error messages)
 * Replaces the common pattern of actionResult state + setTimeout
 *
 * @param {number} timeout - Auto-dismiss timeout in ms (default: 5000)
 * @returns {Object} feedback state and control functions
 */
export const useActionFeedback = (timeout = 5000) => {
    const [feedback, setFeedback] = useState(null);

    const showSuccess = useCallback((message) => {
        setFeedback({ message, isError: false });
        if (timeout > 0) {
            setTimeout(() => setFeedback(null), timeout);
        }
    }, [timeout]);

    const showError = useCallback((message) => {
        setFeedback({ message, isError: true });
        if (timeout > 0) {
            setTimeout(() => setFeedback(null), timeout);
        }
    }, [timeout]);

    const clearFeedback = useCallback(() => {
        setFeedback(null);
    }, []);

    return {
        feedback,
        showSuccess,
        showError,
        clearFeedback,
        // Convenience: check if there's any feedback
        hasFeedback: feedback !== null,
        // Convenience: check feedback type
        isError: feedback?.isError ?? false,
        message: feedback?.message ?? ''
    };
};

export default useActionFeedback;
